import ShareIcon from "@mui/icons-material/Share";
import { Box, styled, Typography, Popover } from "@mui/material";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from "react-share";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import copyToClipboard from "copy-to-clipboard";
import { useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { WEB_URL_BASE } from "@/app/layout/LayoutConstants";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";

const SHARE_ICON_SIZE = 32;

export interface ShareItemModel {
  id: string;
  title: string;
  descriptionShort: string;
  url: string;
}

const List = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    disaply: "flex",
    flexDirection: "column",
    padding: theme.spacing(1, 0),
  }),
);

const Item = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    "> *": {
      display: "flex",
      alignItems: "center",
      gap: 2,
      cursor: "pointer",
      width: "100%",
      padding: theme.spacing(1, 2) + " !important",
    },
    "&:hover": {
      backgroundColor: "grey.100",
    },
    ".MuiSvgIcon-root": {
      width: SHARE_ICON_SIZE,
      height: SHARE_ICON_SIZE,
      backgroundColor: "grey.300",
      padding: 1,
      borderRadius: "100px",
    },
  }),
);

function TrackShareClick({
  item,
  provider,
}: {
  item: ShareItemModel;
  provider: ShareProvider;
}) {
  CreateAnalyticEvent({
    event: {
      adIds: [item.id],
      eventType: "ITEM#ENGAGEMENT#SHARE#CLICK",
      metadata: {
        provider,
      },
    },
  });
}

function ExternalShareItem({
  item,
  provider,
  children,
}: {
  item: ShareItemModel;
  provider: ShareProvider;
  children: React.ReactNode;
}) {
  return (
    <Item onClick={() => TrackShareClick({ item, provider })}>
      {children}
    </Item>
  );
}

function CopyLinkButton({
  item,
  shareUrl,
}: {
  item: ShareItemModel;
  shareUrl: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    const wasCopied = copyToClipboard(shareUrl);
    if (wasCopied) {
      setAnchorEl(event.currentTarget);
    }
    TrackShareClick({ item, provider: "COPY" });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            padding: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              display: "flex",
              gap: 1,
              fontWeight: "bold",
            }}
          >
            <CheckIcon />
            Copied to clipboard
          </Typography>
          <Typography variant="body2">
            <a href={shareUrl} target="_blank" rel="noreferrer">
              {shareUrl}
            </a>
          </Typography>
        </Box>
      </Popover>
      <Item onClick={handleOnClick}>
        <Box>
          <LinkOutlinedIcon />
          <Typography>Copy Link</Typography>
        </Box>
      </Item>
    </>
  );
}

function NativeShareButton({
  item,
  shareUrl,
}: {
  item: ShareItemModel;
  shareUrl: string;
}) {
  return (
    <Item
      onClick={() => {
        navigator.share({
          url: shareUrl,
          text: item.title,
          title: item.title,
        });
        TrackShareClick({ item, provider: "NATIVE" });
      }}
    >
      <Box>
        <ShareIcon />
        <Typography>More</Typography>
      </Box>
    </Item>
  );
}

type ShareProvider =
  | "FACEBOOK"
  | "WHATSAPP"
  | "TWITTER"
  | "EMAIL"
  | "COPY"
  | "NATIVE";

const SHARE_HASHTAG = "horsezone";

export function ShareList({ item }: { item: ShareItemModel }) {
  const shareUrlAbsolute = `${WEB_URL_BASE}${item.url}`;
  return (
    <List>
      <ExternalShareItem item={item} provider="FACEBOOK">
        <FacebookShareButton url={shareUrlAbsolute} hashtag={SHARE_HASHTAG}>
          <FacebookIcon round size={SHARE_ICON_SIZE} />
          <Typography>Facebook</Typography>
        </FacebookShareButton>
      </ExternalShareItem>
      <ExternalShareItem item={item} provider="WHATSAPP">
        <WhatsappShareButton url={shareUrlAbsolute} title={item.title}>
          <WhatsappIcon round size={SHARE_ICON_SIZE} />
          <Typography>WhatsApp</Typography>
        </WhatsappShareButton>
      </ExternalShareItem>
      <ExternalShareItem item={item} provider="TWITTER">
        <TwitterShareButton
          title={item.title}
          url={shareUrlAbsolute}
          hashtags={[SHARE_HASHTAG]}
        >
          <XIcon round size={SHARE_ICON_SIZE} />
          <Typography>X</Typography>
        </TwitterShareButton>
      </ExternalShareItem>
      <ExternalShareItem item={item} provider="EMAIL">
        <EmailShareButton
          url={shareUrlAbsolute}
          body={item.descriptionShort}
          subject={item.title}
        >
          <EmailIcon round size={SHARE_ICON_SIZE} />
          <Typography>Email</Typography>
        </EmailShareButton>
      </ExternalShareItem>
      <CopyLinkButton item={item} shareUrl={shareUrlAbsolute} />
      <NativeShareButton item={item} shareUrl={shareUrlAbsolute} />
    </List>
  );
}
